import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Landmark, Plus, Pencil, Trash2, ArrowUp, ArrowDown, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const EMPTY = { label: "", bank: "", account_name: "", account_number: "", iban: "", swift: "" };
const FIELDS = [
  ["label", "Label (optional)", "e.g. USD account"],
  ["bank", "Bank *", "BNCTL"],
  ["account_name", "Account name *", "KINOS UNIPESSOAL LDA"],
  ["account_number", "Account number *", ""],
  ["iban", "IBAN", ""],
  ["swift", "SWIFT", ""],
];

/** Settings → Bank Accounts: manage the accounts printed on invoices, quotations and statements. */
export function BankAccountsSection({ isAdmin }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null); // null | {} (new) | account
  const [confirmDel, setConfirmDel] = useState(null);
  const { data: rows = [], isLoading } = useQuery({ queryKey: ["bank-accounts"], queryFn: api.listBankAccounts });
  const invalidate = () => qc.invalidateQueries({ queryKey: ["bank-accounts"] });
  const onErr = (e) => toast.error(e?.response?.data?.detail || "Request failed");

  const saveMut = useMutation({
    mutationFn: (form) => (form.id ? api.updateBankAccount(form.id, form) : api.createBankAccount(form)),
    onSuccess: () => { toast.success("Bank account saved"); setEditing(null); invalidate(); },
    onError: onErr,
  });
  const toggleMut = useMutation({
    mutationFn: ({ id, visible }) => api.updateBankAccount(id, { visible }),
    onSuccess: invalidate, onError: onErr,
  });
  const delMut = useMutation({
    mutationFn: (id) => api.deleteBankAccount(id),
    onSuccess: () => { toast.success("Bank account removed"); setConfirmDel(null); invalidate(); },
    onError: onErr,
  });
  const orderMut = useMutation({
    mutationFn: (ids) => api.reorderBankAccounts(ids),
    onSuccess: invalidate, onError: onErr,
  });

  const move = (idx, dir) => {
    const ids = rows.map((r) => r.id);
    const j = idx + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[idx], ids[j]] = [ids[j], ids[idx]];
    orderMut.mutate(ids);
  };

  return (
    <section className="border border-border rounded-md bg-white p-5 mb-6" data-testid="bank-accounts-section">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display font-semibold text-base flex items-center gap-2">
            <Landmark className="size-4 text-primary" /> Bank Accounts
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            Printed in the Bank Details card on invoice, quotation and statement PDFs (and in statement emails).
            Hidden accounts stay saved but are not shown to clients.
          </p>
        </div>
        {isAdmin && (
          <Button size="sm" data-testid="bank-account-add-btn" onClick={() => setEditing({ ...EMPTY })}>
            <Plus className="size-4 mr-1" /> Add account
          </Button>
        )}
      </div>

      {isLoading ? <p className="text-sm text-muted-foreground">Loading…</p> : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="bank-accounts-empty">No bank accounts yet.</p>
      ) : (
        <ul className="divide-y divide-border border border-border rounded-md">
          {rows.map((a, idx) => {
            const visIdx = rows.slice(0, idx + 1).filter((r) => r.visible).length;
            return (
            <li key={a.id} className={`flex flex-wrap items-center gap-3 px-4 py-3 text-sm ${a.visible ? "" : "opacity-60"}`}
                data-testid={`bank-account-row-${a.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-semibold flex items-center gap-2">
                  {a.label || (a.visible ? `Account ${visIdx}` : "Unlabelled account")}
                  <span className="text-xs text-muted-foreground font-normal">{a.bank}</span>
                  {!a.visible && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-600 ring-1 ring-inset ring-slate-200">
                      <EyeOff className="size-3" /> Hidden
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mono mt-0.5">
                  {a.account_name} · No. {a.account_number}
                  {a.iban ? ` · IBAN ${a.iban}` : ""}{a.swift ? ` · SWIFT ${a.swift}` : ""}
                </div>
              </div>
              {isAdmin && (
                <div className="flex items-center gap-1">
                  <div className="flex items-center gap-1.5 mr-2 text-xs text-muted-foreground">
                    {a.visible ? <Eye className="size-3.5" /> : <EyeOff className="size-3.5" />}
                    <Switch data-testid={`bank-account-visible-${a.id}`} checked={!!a.visible}
                            onCheckedChange={(v) => toggleMut.mutate({ id: a.id, visible: v })} />
                  </div>
                  <Button variant="ghost" size="icon" className="size-8" data-testid={`bank-account-up-${a.id}`}
                          disabled={idx === 0} onClick={() => move(idx, -1)} aria-label="Move up">
                    <ArrowUp className="size-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="size-8" data-testid={`bank-account-down-${a.id}`}
                          disabled={idx === rows.length - 1} onClick={() => move(idx, 1)} aria-label="Move down">
                    <ArrowDown className="size-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="size-8" data-testid={`bank-account-edit-${a.id}`}
                          onClick={() => setEditing({ ...a })} aria-label="Edit">
                    <Pencil className="size-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="size-8 text-red-600" data-testid={`bank-account-delete-${a.id}`}
                          onClick={() => setConfirmDel(a)} aria-label="Delete">
                    <Trash2 className="size-3.5" />
                  </Button>
                </div>
              )}
            </li>
            );
          })}
        </ul>
      )}

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="bank-account-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit bank account" : "Add bank account"}</DialogTitle>
            <DialogDescription className="text-xs">Shown exactly as entered on client documents.</DialogDescription>
          </DialogHeader>
          {editing && (
            <form className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                  onSubmit={(e) => { e.preventDefault(); saveMut.mutate(editing); }}>
              {FIELDS.map(([k, l, ph]) => (
                <div key={k} className={k === "account_name" || k === "iban" ? "sm:col-span-2" : ""}>
                  <Label className="overline">{l}</Label>
                  <Input data-testid={`bank-account-field-${k}`} value={editing[k] || ""} placeholder={ph}
                         onChange={(e) => setEditing((s) => ({ ...s, [k]: e.target.value }))} />
                </div>
              ))}
              <DialogFooter className="sm:col-span-2">
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button type="submit" data-testid="bank-account-save" disabled={saveMut.isPending}>
                  {saveMut.isPending ? "Saving…" : "Save"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <DialogContent data-testid="bank-account-delete-dialog">
          <DialogHeader>
            <DialogTitle>Remove this bank account?</DialogTitle>
            <DialogDescription>
              {confirmDel?.bank} · {confirmDel?.account_number} will no longer print on any document.
              Prefer hiding it if you may need it again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDel(null)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" data-testid="bank-account-delete-confirm"
                    disabled={delMut.isPending} onClick={() => delMut.mutate(confirmDel.id)}>Remove</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
